import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { fetchSelfCheckinPrint, printerLog } from '@/apis/aipass'
import html2canvas from 'html2canvas'
import { PrinterContextCreator } from '@/contexts/printer'
import { errorHandler, toErrorMessage } from '@/libs/errors'
import { checkinEposPrinterIdStorage } from '@/constants/self-checkin-device-storage'
import { SelfCheckinCompleteState } from '@/components/pages/self-checkin/complete'
import { CheckinPrint } from '@/models/self-checkin/checkin-print'
import { buildCheckinReceiptSheets } from '@/utils/checkin-receipt-sheets'
import { useResumablePrint } from '@/hooks/use-resumable-print'
import { reportError } from '@/components/apps/error-boundary'
import { PrinterUnavailableNotice } from './printer-unavailable-notice'
import { ReceiptSheet } from './receipt-sheet/receipt-sheet'

type Props = {
  state: SelfCheckinCompleteState
  onComplete: () => void
  isIssueRoomKey?: boolean
}

export const CheckinReceipt: React.FC<Props> = props => {
  const { t } = useTranslation()
  const { printImage, disconnect } = useContext(PrinterContextCreator())
  const [printCheckin, setPrintCheckin] = useState<CheckinPrint>()
  const sheetElements = useRef<Array<HTMLDivElement | null>>([])

  const fetchPrintCheckin = async () => {
    try {
      setPrintCheckin(
        await fetchSelfCheckinPrint({
          hotelId: props.state.hotelId,
          checkinId: props.state.checkinId,
          reservationIdWithAssignIds: props.state.selectedReservationAndRooms,
        }),
      )
    } catch (e) {
      reportError(e)
      alert(t('SelfCheckIn.Failed to print'))
    }
  }

  // チェックインの完了時に1回だけ印刷するため、取得もマウント時の1回だけにする
  useEffect(() => {
    fetchPrintCheckin()
  }, [])

  const sheets = useMemo(
    () =>
      printCheckin
        ? buildCheckinReceiptSheets({ print: printCheckin, complete: props.state, isIssueRoomKey: Boolean(props.isIssueRoomKey) })
        : [],
    [printCheckin, props.state, props.isIssueRoomKey],
  )

  const printSheet = useCallback(
    async (sheetIndex: number, isLatest: () => boolean) => {
      const sheetElement = sheetElements.current[sheetIndex]
      if (!sheetElement) {
        throw new Error(`receipt sheet ${sheetIndex + 1} is not rendered`)
      }
      const receiptImageCanvas = await html2canvas(sheetElement, {
        scale: 1,
        onclone: doc => {
          const div = doc.getElementById('print-content')
          div!.style.display = 'block'
        },
      })
      // プリンターは応答を受け取る onreceive を1つしか持てず、あとから送った処理が応答を受け取って先に送った処理は終わらなくなる。画像化を待つ間に再印刷が始まっていたら、古い処理は送らない
      if (!isLatest()) {
        return
      }
      const { sheetNumber } = sheets[sheetIndex]
      await printImage({
        canvas: receiptImageCanvas,
        filename: `${props.state.checkinId}_${sheetNumber}`,
        hotelId: props.state.hotelId,
        connectIdKey: checkinEposPrinterIdStorage,
        isStayConnected: true,
        reservationUuid: props.state.uploadReservationUuid,
      })
      printerLog({ step: 'success', sheetNumber, sheetCount: sheets.length, state: props.state })
    },
    [sheets, printImage, props.state],
  )

  const handlePrintError = useCallback(
    (e: unknown, sheetIndex: number) => {
      reportError(e)
      errorHandler(e)
      printerLog({
        step: 'failed',
        message: toErrorMessage(e),
        sheetNumber: sheets[sheetIndex].sheetNumber,
        sheetCount: sheets.length,
        state: props.state,
      })
    },
    [sheets, props.state],
  )

  const { print } = useResumablePrint({
    sheetCount: sheets.length,
    printSheet,
    onError: handlePrintError,
    onComplete: props.onComplete,
  })

  // 印刷を始めるのは、紙の一覧ができたときの1回だけ。print は親の再描画で onComplete が変わるたびに作り直されるため、依存に入れない
  useEffect(() => {
    if (sheets.length === 0) {
      return
    }
    printerLog({ step: 'start', sheetCount: sheets.length })
    print()
  }, [sheets])

  const handleReprint = async () => {
    // 応答しない接続を掴んだまま再試行しても同じ結果になるため、切断してから印刷し直す（接続は printImage が張り直す）
    disconnect()
    const isPrinted = await print()
    printerLog({ message: isPrinted ? 'connect' : 'error retry connection.', mode: 'retry' })
  }

  if (!printCheckin) {
    return <></>
  }
  return (
    <div>
      <PrinterUnavailableNotice onClick={handleReprint} />
      <div id="print-content" css={printContentStyle}>
        {sheets.map((sheet, sheetIndex) => (
          <ReceiptSheet
            key={sheet.sheetNumber}
            sheet={sheet}
            ref={element => {
              sheetElements.current[sheetIndex] = element
            }}
          />
        ))}
      </div>
    </div>
  )
}

const printContentStyle = css({
  display: 'none',
})
